// src/pages/settings/system/FailedJobDrawer.tsx
import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Button, Descriptions, Space, Tag, Typography } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { formatInZone } from "../../../utils/time";

import DetailDrawer from "../../../components/shared/DetailDrawer";
import type { QueueApi } from "../../../api/queues/queueApi";
import { extractValidationErrors } from "../../../utils/apiError";

const { Text } = Typography;

interface FailedJobDrawerProps {
	serviceKey: string;
	serviceLabel: string;
	api: QueueApi;
	jobId: string | null;
	onClose: () => void;
	onRetry: (id: string) => void;
	retrying: boolean;
}

const shortClass = (fqcn: string): string => fqcn.split("\\").pop() ?? fqcn;

const FailedJobDrawer: React.FC<FailedJobDrawerProps> = ({ serviceKey, serviceLabel, api, jobId, onClose, onRetry, retrying }) => {
	const detail = useQuery({
		queryKey: ["queue", serviceKey, "failed", "detail", jobId],
		queryFn: () => api.getFailedJob(jobId as string),
		enabled: jobId !== null,
		retry: false,
	});

	const job = detail.data;

	return (
		<DetailDrawer
			open={jobId !== null}
			onClose={onClose}
			width={560}
			title={job ? shortClass(job.name) : "Failed job"}
			subtitle={`${serviceLabel} · ${jobId ?? ""}`}
			loading={detail.isLoading}
			loadingMessage="Loading failed job…"
			errorMessages={detail.error ? extractValidationErrors(detail.error) : []}
			headerActions={
				job && (
					<Button type="primary" icon={<ReloadOutlined />} loading={retrying} onClick={() => onRetry(job.id)}>
						Retry
					</Button>
				)
			}
		>
			{job && (
				<Descriptions column={1} size="small" bordered labelStyle={{ width: 140 }}>
					<Descriptions.Item label="Class">
						<Text code>{job.payload_command}</Text>
					</Descriptions.Item>
					<Descriptions.Item label="Queue">{`${job.queue} · ${job.connection}`}</Descriptions.Item>
					<Descriptions.Item label="Failed at">{formatInZone(job.failed_at, "DD MMM YYYY HH:mm:ss")}</Descriptions.Item>
					<Descriptions.Item label="Attempts">{job.attempts}</Descriptions.Item>
					<Descriptions.Item label="Exception">
						<Text code>{job.exception_class}</Text>
					</Descriptions.Item>
					<Descriptions.Item label="Summary">{job.exception_summary || "—"}</Descriptions.Item>
					<Descriptions.Item label="Tags">
						{job.tags.length === 0 ? (
							"—"
						) : (
							<Space wrap size={4}>
								{job.tags.map((tag) => (
									<Tag key={tag}>{tag}</Tag>
								))}
							</Space>
						)}
					</Descriptions.Item>
					<Descriptions.Item label="Retried to">
						{job.retried_to.length === 0 ? "Not retried" : job.retried_to.map((id) => <div key={id}><Text code>{id}</Text></div>)}
					</Descriptions.Item>
				</Descriptions>
			)}
		</DetailDrawer>
	);
};

export default FailedJobDrawer;
